import { useState, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';

const ITERATIVE_TIMEOUT_MS = 1200000; // 20 minutos

export const useVrpIterative = (taxiOSId) => {
    const [state, setState] = useState({
        status: 'idle',
        currentCapacity: null,
        routes: [],
        pendingCount: 0,
        acceptedCount: 0,
        totalPassengers: 0,
        nextCapacities: [],
        selectedIndices: [],
        error: null,
        isPersisting: false,
        persistResult: null,
    });

    const start = useCallback(async () => {
        setState(prev => ({ ...prev, status: 'loading', error: null }));
        try {
            const response = await axiosInstance.post(
                `/administration/taxi-service/optimize-iterative/start/${taxiOSId}`,
                {},
                {
                    timeout: ITERATIVE_TIMEOUT_MS,
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            const data = response.data;
            if (!data.success) {
                throw new Error(data.message || 'Error al iniciar optimización');
            }
            if (data.finished) {
                setState(prev => ({
                    ...prev,
                    status: 'finished',
                    pendingCount: 0,
                    acceptedCount: data.acceptedCount || data.totalPassengers || 0,
                    totalPassengers: data.totalPassengers || 0,
                }));
                return data;
            }
            setState(prev => ({
                ...prev,
                status: 'selecting',
                currentCapacity: data.capacity,
                routes: data.routes || [],
                pendingCount: data.remainingPassengers || data.totalPassengers || 0,
                acceptedCount: 0,
                totalPassengers: data.totalPassengers || 0,
                nextCapacities: data.nextCapacities || [],
                selectedIndices: [],
            }));
            return data;
        } catch (error) {
            setState(prev => ({
                ...prev,
                status: 'error',
                error: error.response?.data?.message || error.message || 'Error al iniciar'
            }));
            throw error;
        }
    }, [taxiOSId]);

    const select = useCallback(async (selectedIndices) => {
        // Evitar llamadas recursivas infinitas
        setState(prev => ({ ...prev, status: 'loading', error: null }));
        try {
            const response = await axiosInstance.post(
                `/administration/taxi-service/optimize-iterative/select/${taxiOSId}`,
                { selectedRouteIndices: selectedIndices },
                {
                    timeout: ITERATIVE_TIMEOUT_MS,
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            const data = response.data;
            if (!data.success) {
                throw new Error(data.message || 'Error al seleccionar rutas');
            }

            // Si finalizó, actualizar estado con los contadores
            if (data.finished) {
                setState(prev => ({
                    ...prev,
                    status: 'finished',
                    pendingCount: 0,
                    acceptedCount: data.acceptedCount || prev.acceptedCount || 0,
                    totalPassengers: data.totalPassengers || prev.totalPassengers || 0,
                }));
                return data;
            }

            // Si el backend indica que debe saltar automáticamente (sin rutas), llamar a select con array vacío
            if (data.shouldSelectNext || (data.routes && data.routes.length === 0)) {
                // Pequeño delay para evitar recursión inmediata
                await new Promise(resolve => setTimeout(resolve, 100));
                return await select([]);
            }

            // Actualizar estado con nuevas rutas
            setState(prev => ({
                ...prev,
                status: 'selecting',
                currentCapacity: data.capacity,
                routes: data.routes || [],
                pendingCount: data.pendingCount || 0,
                acceptedCount: data.acceptedCount || prev.acceptedCount,
                nextCapacities: data.nextCapacities || [],
                selectedIndices: [],
            }));
            return data;
        } catch (error) {
            setState(prev => ({
                ...prev,
                status: 'error',
                error: error.response?.data?.message || error.message || 'Error al seleccionar'
            }));
            throw error;
        }
    }, [taxiOSId]);

    const commit = useCallback(async () => {
        setState(prev => ({ ...prev, isPersisting: true, error: null }));
        try {
            const response = await axiosInstance.post(
                `/administration/taxi-service/optimize-iterative/commit/${taxiOSId}`,
                {},
                {
                    timeout: ITERATIVE_TIMEOUT_MS,
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            const data = response.data;
            if (!data.success) {
                throw new Error(data.message || 'Error al persistir');
            }
            setState(prev => ({
                ...prev,
                status: 'persisted',
                isPersisting: false,
                persistResult: data,
            }));
            return data;
        } catch (error) {
            setState(prev => ({
                ...prev,
                isPersisting: false,
                error: error.response?.data?.message || error.message || 'Error al persistir'
            }));
            throw error;
        }
    }, [taxiOSId]);

    const reset = useCallback(() => {
        setState({
            status: 'idle',
            currentCapacity: null,
            routes: [],
            pendingCount: 0,
            acceptedCount: 0,
            totalPassengers: 0,
            nextCapacities: [],
            selectedIndices: [],
            error: null,
            isPersisting: false,
            persistResult: null,
        });
    }, []);

    const cancel = useCallback(async () => {
        try {
            await axiosInstance.post(
                `/administration/taxi-service/optimize-iterative/cancel/${taxiOSId}`,
                {},
                { timeout: 5000 }
            );
        } catch (e) {
            console.warn('Error al cancelar proceso iterativo:', e);
        }
    }, [taxiOSId]);

    return {
        ...state,
        start,
        select,
        commit,
        cancel,
        reset,
        setSelectedIndices: (indices) => setState(prev => ({ ...prev, selectedIndices: indices })),
    };
};